// The streaks of the current looks (R2716; ui-map-interface.md §17.3.07;
// mockup round 117, the page's `Particles` engine of cards 01 to 10 and 15).
//
// A streak is a point that the current carries, with a tail of the points
// it passed. It lives a short random time, then comes back at a random
// point of the view, so the cover stays even. The tail is a ring of
// `tail` points, one recorded each `every_sec` wall seconds.
//
// **THE PACE IS A SCREEN PACE.** A streak runs `pace` screen px a wall
// second for each px a map hour of current, at every zoom: the field stands
// still between two tide steps, so all motion is a time-lapse. The count is
// `density` for each `area_px` of the screen, so the cover per screen is
// the same at every zoom, and the budget of tail segments a frame caps it.
//
// **ONE BATCH.** The tails go into the segment sink of the chart, which is
// one instanced draw for every ink and alpha: no streak is a draw call of
// its own (§10). The positions are typed arrays that the frame reuses, so a
// step allocates nothing (§10.3).
//
// **THE STILL FORM.** Under reduced motion a streak does not move: its tail
// is traced back along the current when it is placed, and it stands at full
// life. The look keeps its picture, and nothing moves.
//
// **A ZOOM OUT TURNS THE STREAKS OVER.** A streak keeps its world point
// when the zoom changes, so a zoom out leaves the old streaks in a patch at
// the middle of the view. When the view widens past `rezoom_ratio`, each
// streak ends its life within `fade_sec` plus a random share of
// `rezoom_spread_sec`, with its own fade, and comes back across the whole
// view (R2836). A zoom in needs nothing: a streak that leaves the view
// comes back at once.
//
// The engine is pure: it reads a `FlowGrid` and writes into a sink, so a
// test steps it with a seed and no engine.

import { CHART } from '../../../gen/presentation';
import { F_ROUND, F_SCREEN, type ViewRect } from '../../layer';
import type { CircleSink, SegmentSink } from '../../layers/batches';
import { mulberry32 } from '../../../world';
import { flowAt, smooth, type FlowGrid } from './current-field';
import type { BandGains } from './current-depth';

/** The figures of the piece's own table. */
const T = CHART.pieces.current_arrows;

/** The figures of one look's streaks: one entry of each array of `.arr`. */
export interface StreakLook {
  density: number;
  tail: number;
  everySec: number;
  lifeMin: number;
  lifeMax: number;
  pace: number;
  widthPx: number;
  alpha: number;
  alphaFloor: number;
  fastFrom: number;
  fastTo: number;
  fadePow: number;
  jitterPx: number;
  dot: boolean;
  glimmer: boolean;
  seed: number;
}

/** The streak figures of the look at `index` of `CURRENT_LOOKS` (R2869). */
export function streakLook(index: number): StreakLook {
  const a = T.arr;
  const at = (xs: readonly number[]): number => xs[index] ?? 0;
  return {
    density: at(a.density), tail: Math.max(1, Math.round(at(a.tail))), everySec: at(a.every_sec),
    lifeMin: at(a.life_min), lifeMax: at(a.life_max), pace: at(a.pace),
    widthPx: at(a.width_px), alpha: at(a.alpha), alphaFloor: at(a.alpha_floor),
    fastFrom: at(a.fast_from), fastTo: at(a.fast_to), fadePow: at(a.fade_pow),
    jitterPx: at(a.jitter_px), dot: at(a.dot) > 0, glimmer: at(a.glimmer) > 0, seed: at(a.seed),
  };
}

/**
 * How many streaks a look keeps on a screen of `screenPx` CSS px squared:
 * `density` for each `area_px`, at least `min_streaks`, and under the
 * budget of tail segments of the form factor. A look with no density keeps
 * none.
 */
export function streakCount(look: StreakLook, screenPx: number, sheet: boolean): number {
  if (!(look.density > 0)) return 0;
  const f = T.fig;
  const want = Math.max(f.min_streaks, Math.round((look.density * screenPx) / f.area_px));
  const budget = sheet ? f.budget_segments_sheet : f.budget_segments;
  const per = look.dot ? 1 : look.tail;
  return Math.max(0, Math.min(want, Math.floor(budget / per)));
}

/** What one step and one emit read of the world. */
export interface StreakEnv {
  grid: FlowGrid;
  /** The factor of the tide step now; a negative factor turns the flow. */
  tide: number;
  /** The strongest current of the map at tide 1, px a map hour. */
  strongest: number;
  /** CSS px for one world px. */
  zoom: number;
  /** The visible world rect. */
  view: ViewRect;
}

/** The streaks of one look on one screen. */
export class Streaks {
  readonly n: number;
  readonly look: StreakLook;
  readonly still: boolean;
  /** Positions, in world px. */
  readonly x: Float32Array;
  readonly y: Float32Array;
  readonly age: Float32Array;
  readonly life: Float32Array;
  /** The speed share at the last step: 0 slack, 1 the strongest cell. */
  readonly u: Float32Array;
  /** The tail ring: `tail` points a streak, x then y. */
  readonly hist: Float32Array;
  /** The slot of the ring written last. */
  ring = 0;
  private acc = 0;
  /** The zoom the cover is even for: the nearest zoom since the last turn
   *  over. 0 before the first step. */
  private zoomRef = 0;
  private readonly rnd: () => number;
  private readonly f: [number, number] = [0, 0];

  constructor(look: StreakLook, n: number, still: boolean) {
    this.look = look;
    this.n = n;
    this.still = still;
    this.rnd = mulberry32(look.seed);
    this.x = new Float32Array(n);
    this.y = new Float32Array(n);
    this.age = new Float32Array(n);
    this.life = new Float32Array(n);
    this.u = new Float32Array(n);
    this.hist = new Float32Array(n * look.tail * 2);
  }

  /** Places every streak in the view at a random age, then runs the warm
   *  steps, so the tails stand on the first frame. */
  seed(env: StreakEnv): void {
    this.zoomRef = env.zoom;
    for (let i = 0; i < this.n; i++) {
      this.spawn(i, env);
      if (!this.still) this.age[i] = this.rnd() * this.life[i];
    }
    if (this.still) return;
    const steps = Math.round(T.fig.warm_steps);
    for (let s = 0; s < steps; s++) this.step(T.fig.warm_sec, env);
  }

  /** Puts streak `i` back on a random water point of the view. */
  spawn(i: number, env: StreakEnv): void {
    const v = env.view, f = this.f, look = this.look;
    const tries = Math.max(1, Math.round(T.fig.spawn_tries));
    let x = 0, y = 0, u = 0;
    for (let t = 0; t < tries; t++) {
      x = v.x0 + this.rnd() * (v.x1 - v.x0);
      y = v.y0 + this.rnd() * (v.y1 - v.y0);
      if (flowAt(env.grid, x, y, f)) { u = speedShare(f, env); break; }
    }
    this.x[i] = x;
    this.y[i] = y;
    this.u[i] = u;
    this.age[i] = 0;
    this.life[i] = look.lifeMin + this.rnd() * (look.lifeMax - look.lifeMin);
    const K = look.tail, b = i * K * 2;
    if (this.still) {
      // The still form: full life, and a tail traced back along the
      // current, as far as the moving streak would have run.
      this.age[i] = this.life[i] / 2;
      const k = look.pace / Math.max(1e-6, env.zoom);
      let px = x, py = y;
      for (let m = 1; m <= K; m++) {
        if (flowAt(env.grid, px, py, f)) {
          px -= f[0] * env.tide * k * look.everySec;
          py -= f[1] * env.tide * k * look.everySec;
        }
        const slot = (((this.ring - m + 1) % K) + K) % K;
        this.hist[b + slot * 2] = px;
        this.hist[b + slot * 2 + 1] = py;
      }
      return;
    }
    for (let m = 0; m < K; m++) { this.hist[b + m * 2] = x; this.hist[b + m * 2 + 1] = y; }
  }

  /** One step of `dt` wall seconds. The still form does not move: it only
   *  places again a streak that a pan left out of the view. */
  step(dt: number, env: StreakEnv): void {
    const look = this.look, f = this.f, v = env.view;
    const m = T.fig.margin_px / Math.max(1e-6, env.zoom);
    this.rezoom(env);
    if (this.still) {
      for (let i = 0; i < this.n; i++) {
        const x = this.x[i], y = this.y[i];
        if (x < v.x0 - m || x > v.x1 + m || y < v.y0 - m || y > v.y1 + m) this.spawn(i, env);
      }
      return;
    }
    const k = look.pace / Math.max(1e-6, env.zoom);
    const jit = look.jitterPx / Math.max(1e-6, env.zoom);
    for (let i = 0; i < this.n; i++) {
      const ok = flowAt(env.grid, this.x[i], this.y[i], f);
      this.age[i] += dt;
      const x = this.x[i], y = this.y[i];
      if (!ok || this.age[i] > this.life[i] || x < v.x0 - m || x > v.x1 + m || y < v.y0 - m || y > v.y1 + m) {
        this.spawn(i, env);
        continue;
      }
      const fx = f[0] * env.tide, fy = f[1] * env.tide;
      this.u[i] = speedShare(f, env);
      this.x[i] = x + fx * k * dt;
      this.y[i] = y + fy * k * dt;
      if (jit > 0) {
        this.x[i] += (this.rnd() - 0.5) * jit * dt;
        this.y[i] += (this.rnd() - 0.5) * jit * dt;
      }
    }
    this.acc += dt;
    if (this.acc >= look.everySec) {
      this.acc = 0;
      const K = look.tail;
      this.ring = (this.ring + 1) % K;
      for (let i = 0; i < this.n; i++) {
        const b = (i * K + this.ring) * 2;
        this.hist[b] = this.x[i];
        this.hist[b + 1] = this.y[i];
      }
    }
  }

  /**
   * Turns the streaks over when the view widened past `rezoom_ratio` since
   * the nearest zoom. A moving streak ends within `fade_sec` plus a random
   * share of `rezoom_spread_sec`; the cap never cuts into a fade, so no
   * alpha jumps. The still form places every streak again at once.
   */
  private rezoom(env: StreakEnv): void {
    const z = env.zoom;
    if (!(this.zoomRef > 0) || z > this.zoomRef) { this.zoomRef = z; return; }
    if (this.zoomRef / Math.max(1e-6, z) < T.fig.rezoom_ratio) return;
    this.zoomRef = z;
    if (this.still) {
      for (let i = 0; i < this.n; i++) this.spawn(i, env);
      return;
    }
    const fade = Math.max(1e-6, T.fig.fade_sec), spread = T.fig.rezoom_spread_sec;
    for (let i = 0; i < this.n; i++) {
      this.life[i] = Math.min(this.life[i], this.age[i] + fade + this.rnd() * spread);
    }
  }

  /** The alpha of streak `i` at wall second `t`, before its level. */
  alphaOf(i: number, t: number): number {
    const look = this.look, fig = T.fig, u = this.u[i];
    const fade = Math.max(1e-6, fig.fade_sec);
    const lf = Math.min(1, this.age[i] / fade, (this.life[i] - this.age[i]) / fade);
    let a = look.fastTo > 0
      ? smooth(look.fastFrom, look.fastTo, u)
      : look.alphaFloor + (1 - look.alphaFloor) * Math.min(1, u * fig.alpha_gain);
    if (look.glimmer) {
      const kinds = Math.max(1, Math.round(fig.glimmer_kinds));
      const hz = fig.glimmer_hz + (i % kinds) * fig.glimmer_hz_step;
      a *= fig.glimmer_floor + (1 - fig.glimmer_floor) * (0.5 + 0.5 * Math.sin(t * hz + i * fig.glimmer_phase));
    }
    return Math.max(0, lf) * a;
  }

  /**
   * Writes the tails (or the motes) into the sinks: one instance a tail
   * step, in `ink`. Answers how many streaks drew. `gain` is the alpha
   * gain of the band under each head while the depth tints draw (R2871),
   * or null for gain 1.
   */
  emit(
    segs: SegmentSink, circles: CircleSink, ink: number, t: number, alphaScale: number,
    gain: BandGains | null = null,
  ): number {
    const look = this.look, fig = T.fig, K = look.tail;
    const L = Math.max(1, Math.round(fig.levels));
    const still = this.still;
    let shown = 0;
    for (let i = 0; i < this.n; i++) {
      const a = this.alphaOf(i, still ? 0 : t);
      if (a < fig.alpha_cut) continue;
      const la = (Math.min(L - 1, Math.floor(a * L)) + 1) / L;
      const base = look.alpha * la * alphaScale * (gain ? gain.at(this.x[i], this.y[i]) : 1);
      shown++;
      if (look.dot) {
        circles.disc(this.x[i], this.y[i], look.widthPx / 2, ink, base, F_SCREEN);
        continue;
      }
      const b = i * K * 2;
      for (let j = 0; j < K; j++) {
        const ta = Math.pow(1 - j / K, look.fadePow);
        if (ta < fig.tail_cut) break;
        const r0 = (((this.ring - j + 1) % K) + K) % K, r1 = (((this.ring - j) % K) + K) % K;
        const x0 = j === 0 ? this.x[i] : this.hist[b + r0 * 2];
        const y0 = j === 0 ? this.y[i] : this.hist[b + r0 * 2 + 1];
        segs.line(x0, y0, this.hist[b + r1 * 2], this.hist[b + r1 * 2 + 1],
          look.widthPx, ink, base * ta, F_SCREEN | F_ROUND);
      }
    }
    return shown;
  }
}

/** The speed share of a current at the tide: 0 slack, 1 the strongest
 *  cell of the map. */
export function speedShare(f: readonly [number, number], env: StreakEnv): number {
  if (!(env.strongest > 0)) return 0;
  return Math.min(1, (Math.hypot(f[0], f[1]) * Math.abs(env.tide)) / env.strongest);
}
